/**
 * 快捷标签组件
 * 横向滚动标签栏，pill 设计
 *
 * 设计规范（来自 pencil）：
 * - 容器：背景 #FFFFFF，padding 8px 16px，顶部边框 #E4E4E7
 * - 标签样式：pill design，圆角 100
 * - 选中状态：背景 #EFF6FF + 文字颜色 #2563EB + 边框 #2563EB
 * - 未选中状态：背景 #F8F9FA + 文字颜色 #71717A + 边框 #E4E4E7
 *
 * 可访问性：
 * - role="list" 用于标签列表
 * - aria-label 标识当前选中状态
 */

import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { faqApi } from '../../services/api';
import { PinnedFaqItem } from '../../services/types';

export interface QuickTagsProps {
  /** 当前选中的标签 */
  selectedTag?: string;
  /** 标签选择回调 */
  onSelect: (tag: string) => void;
  /** 自定义 CSS 类名 */
  className?: string;
}

export const QuickTags: React.FC<QuickTagsProps> = ({
  selectedTag,
  onSelect,
  className,
}) => {
  const [tags, setTags] = useState<PinnedFaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPinnedTags = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await faqApi.getPublicPinned();
        if (res.success && res.data) {
          setTags(res.data.items || []);
        }
      } catch (err) {
        console.error('加载置顶标签失败:', err);
        setError('加载标签失败');
        // 使用空数组作为降级处理
        setTags([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPinnedTags();
  }, []);

  if (loading) {
    return (
      <div
        className={clsx(
          'flex items-center gap-[8px]',
          'px-[16px]',
          'py-[8px]',
          'bg-white',
          'border-t border-[#E4E4E7]',
          className
        )}
        role="list"
        aria-label="快捷问题标签"
      >
        <div className="text-[12px] leading-[16px] text-[#71717A]">加载中...</div>
      </div>
    );
  }

  if (error || tags.length === 0) {
    return null;
  }

  return (
    <div
      className={clsx(
        'flex items-center gap-[8px]',
        'px-[16px]',
        'py-[8px]',
        'bg-white',
        'border-t border-[#E4E4E7]',
        'overflow-x-auto',
        'scrollbar-hide',
        className
      )}
      role="list"
      aria-label="快捷问题标签"
    >
      {tags.map((tag) => {
        const isSelected = selectedTag === tag.question;

        return (
          <button
            key={tag.id}
            onClick={() => onSelect(tag.question)}
            className={clsx(
              'flex-shrink-0 px-[12px] py-[6px] rounded-full',
              'text-[12px] leading-[16px] font-medium transition-all duration-200',
              'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-opacity-50',
              'hover:scale-105 active:scale-95',
              // 选中状态
              isSelected
                ? 'bg-[#EFF6FF] text-[#2563EB] border border-[#2563EB]'
                : 'bg-[#F8F9FA] text-[#71717A] border border-[#E4E4E7] hover:bg-[#EFF6FF] hover:text-[#2563EB] hover:border-[#2563EB]'
            )}
            role="listitem"
            aria-label={`快捷问题: ${tag.question}${isSelected ? '（已选中）' : ''}`}
            aria-pressed={isSelected}
          >
            {tag.question}
          </button>
        );
      })}

      {/* 隐藏滚动条样式 */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};
